import { money, round2 } from './nurseryMoney';
import type { CashBreakdown } from '../types/nurseryCounter';
import { nurseryText } from '../i18n/shared';

/**
 * Cash arithmetic for the counter: counting a till by notes and coins, and
 * the round figures a customer is likely to hand over.
 */

/** Notes and coins in cents, largest first: the server's list, as a fallback. */
export const AUD_DENOMINATIONS = [10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5];

export const denominationLabel = (cents: number) => (cents >= 100 ? `$${cents / 100}` : `${cents}c`);

/** What a count adds up to, in dollars. */
export function breakdownTotal(breakdown: CashBreakdown): number {
  return round2(Object.entries(breakdown).reduce((sum, [cents, count]) => sum + Number(cents) * (Number(count) || 0), 0) / 100);
}

/** The count without empty denominations, or null when nothing was counted. */
export function cleanBreakdown(breakdown: CashBreakdown): CashBreakdown | null {
  const out: CashBreakdown = {};
  for (const [cents, count] of Object.entries(breakdown)) if (Number(count) > 0) out[cents] = Number(count);
  return Object.keys(out).length ? out : null;
}

/** What a customer might hand over: the exact sum, then the next round figures above it. */
export function quickTenders(due: number): number[] {
  const out = new Set<number>([round2(due)]);
  for (const step of [5, 10, 20, 50, 100]) {
    const up = Math.ceil(due / step) * step;
    if (up > due + 0.004) out.add(up);
  }
  return [...out].sort((a, b) => a - b).slice(0, 5);
}

/** A typed amount: digits and one point, at most two decimals. null when it is not one. */
export function parseAmount(text: string): number | null {
  const t = text.trim();
  if (!/^\d{1,7}(\.\d{0,2})?$/.test(t)) return null;
  return round2(Number(t));
}

/** "Short $4.35", "Over $2.00", "Balanced". */
export function varianceText(variance: number | null): string {
  if (variance == null) return '';
  if (Math.abs(variance) < 0.005) return nurseryText('variance.balanced');
  return nurseryText(variance > 0 ? 'variance.over' : 'variance.short', { amount: money(Math.abs(variance)) });
}
